// src/components/map/hooks/adapters/useWellDetailHover.ts
//
// Adapter hook extracted from WellDetail.tsx.
// Encapsulates all imperative Mapbox interactions (mouse events, feature queries,
// debounced tooltip data fetching, overflow adjustment) leaving the component
// responsible only for rendering the tooltip UI.
import { useEffect, useMemo, useRef, useState } from "react";
import type { QueryKey } from "react-query";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import type { AxiosResponse } from "axios";
import { NOT_AVAILABLE } from "constants/app.constants";
import { ENTITY_NAME_PROPERTY } from "constants/chart.constants";
import {
  FACILITY_LAYER,
  GROUP_SHAPE_DISCRIMINATORS,
  SPIDER_FACILITY_LAYER,
  STICK_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";
import _debounce from "lodash/debounce";
import type { PointLike } from "mapbox-gl";
import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";
import {
  selectWellCardDrawArmed,
  selectWellCardPreviewOpen,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import { MapInteraction, MouseInteraction } from "types/maps/interactions";
import { arrayOrEmpty } from "utils";

import useColumns, { useFacilityColumns } from "hooks/useColumns";

import { UwiInfo, WellListResult } from "api/filter";
import { fetchGroupDetail, fetchWellDetail } from "api/wellDetail";

import { useGeomBinContext } from "components/geom-bin/hooks/useGeomBinContext";
import {
  shapefileColumnFromKey,
  useIsShapefileFieldAbsentFromProject,
  useResolveShapefileColumn
} from "components/project/shapefiles/hooks/useResolveShapefileColumn";
import { useUserDefaults } from "components/user/hooks";
import { WELL_LIST_QUERY_KEY } from "components/well-list/hooks/useGetWellsFromFilterId";

import { events, modes } from "../../utils/drawConstants";
import { getColumnName } from "../../utils/getColumnName";
import { withoutAbsentShapefileFields } from "../../utils/withoutAbsentShapefileFields";
import { useMapContext } from "../useMapContext";

// ── Constants ────────────────────────────────────────────────────────────────

const MARGIN_FROM_VIEWPORT = 30;
const TOOLTIP_OFFSET_X = 10;
const TOOLTIP_OFFSET_Y = 50;
const DEBOUNCE_DELAY_MS = 200;
const MIN_ZOOM_FOR_TOOLTIP = 6;

/** How MapboxDraw spells its current mode as a class on the map container. */
const DRAW_MODE_CLASS_PREFIX = "mode-";

/**
 * The two draw modes that mean no tool is armed, as class names.
 *
 * `direct_select` - dragging the vertices of a shape already drawn - counts as
 * armed on purpose, the same way `useMapClickSelection` counts it: that handler
 * swallows the click, so a pointer there would promise a selection the map has
 * already decided not to make.
 */
const IDLE_DRAW_MODE_CLASSES: readonly string[] = [
  `${DRAW_MODE_CLASS_PREFIX}${modes.SIMPLE_SELECT}`,
  `${DRAW_MODE_CLASS_PREFIX}${modes.STATIC}`
];

/**
 * Is one of the map's draw tools armed and waiting to be used?
 *
 * Every tool on the map shares one MapboxDraw control - the toolbar's lasso,
 * circle and square, the geom-bin polygon, and the section line - and the
 * control writes its own mode onto the map container as a class. That class is
 * also where its crosshair comes from, through the draw stylesheet, so reading
 * it is asking the tool itself whether it owns the cursor. The same rule
 * `useMapClickSelection` uses to decide a draw is in progress: any mode but
 * idle select or static.
 *
 * A class read, not a map query: no feature lookup and no work on mousemove.
 * The toolbar's lasso is the reason this exists at all - it holds "armed" in
 * component state that nothing outside it can see.
 */
function isDrawToolArmed(mapbox: Maybe<mapboxgl.Map>): boolean {
  const container = mapbox?.getContainer?.();
  if (!container) return false;
  return Array.from(container.classList).some(
    (name) =>
      name.startsWith(DRAW_MODE_CLASS_PREFIX) && !IDLE_DRAW_MODE_CLASSES.includes(name)
  );
}

/** The well list is the display-name source for a hovered stick; returns the formattedId of the first cached `getWellsFromFilterId` entry whose uniqueId matches, preferring the page for the current txnId, or nothing if no page has a usable match. */
function findWellListLabel(
  queries: ReadonlyArray<[QueryKey, Maybe<AxiosResponse<WellListResult>>]>,
  uwi: string,
  txnId: Maybe<string>
): Maybe<string> {
  const preferred = queries.find(([key]) => key[2] === txnId);
  const ordered = preferred
    ? [preferred, ...queries.filter((query) => query !== preferred)]
    : queries;

  for (const [, response] of ordered) {
    const uwiList = response?.data?.uwiList;
    const match = arrayOrEmpty<UwiInfo>(uwiList).find((well) => well.uniqueId === uwi);
    const formattedId = match?.formattedId;
    if (formattedId && formattedId.trim() !== "") return formattedId;
  }
  return undefined;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface WellDetailState {
  readonly uwi: string;
  readonly formattedUwi: string;
  readonly show: boolean;
  readonly x: number;
  readonly y: number;
  readonly fields: Readonly<Record<string, string>>;
  readonly isFacility: boolean;
  readonly isGroup: boolean;
}

/** Mutable version used internally during construction before freezing into state. */
interface MutableWellDetail {
  uwi: string;
  formattedUwi: string;
  show: boolean;
  x: number;
  y: number;
  fields: Record<string, string>;
  isFacility: boolean;
  isGroup: boolean;
}

export interface WellDetailDisplayField {
  readonly label: string;
  readonly value: string;
}

/** Render-ready view model exposed to the component. */
export interface WellDetailViewModel {
  readonly show: boolean;
  readonly x: number;
  readonly y: number;
  readonly isFacility: boolean;
  readonly isGroup: boolean;
  readonly displayFields: ReadonlyArray<WellDetailDisplayField>;
}

export interface UseWellDetailHoverResult {
  /** Current tooltip view model (position, resolved display fields, visibility). */
  readonly wellDetail: WellDetailViewModel;
  /** Ref to attach to the tooltip DOM element for overflow adjustment. */
  readonly popupRef: React.RefObject<HTMLDivElement>;
}

// ── Initial state ────────────────────────────────────────────────────────────

const INITIAL_WELL_DETAIL: WellDetailState = {
  uwi: "",
  formattedUwi: "",
  show: false,
  x: 0,
  y: 0,
  fields: {},
  isFacility: false,
  isGroup: false
};

const HIDDEN_DETAIL_ARGS = {
  position: { x: 0, y: 0 },
  show: false,
  uwi: "",
  isFacility: false
} as const;

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Adapter hook that manages the well/facility hover tooltip on the Mapbox map.
 *
 * Responsibilities:
 * - Registers Mapbox mouse-move, mouse-leave, and map-movement event listeners.
 * - Queries rendered features under the cursor via `queryRenderedFeatures`.
 * - Debounces tooltip data fetching (calls `fetchWellDetail` API).
 * - Adjusts tooltip position to stay within the map viewport.
 * - Re-fetches when significant-figures precision settings change.
 *
 * The consuming component only needs to render the tooltip UI based on the
 * returned `wellDetail` state and attach `popupRef` to the tooltip element.
 */
export function useWellDetailHover(): UseWellDetailHoverResult {
  const { mapbox, entitiesInGroup } = useMapContext();
  const txnId = useMapStore(selectTxnId);
  const queryClient = useQueryClient();

  // ── External data dependencies ───────────────────────────────────────

  const defaults = useUserDefaults();
  const significantFiguresSettings = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );

  // ── Refs for stable access inside event handlers ─────────────────────

  const significantFiguresSettingsRef = useRef(significantFiguresSettings);
  useEffect(() => {
    significantFiguresSettingsRef.current = significantFiguresSettings;
  }, [significantFiguresSettings]);

  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const globalGroupByRef = useRef(globalGroupBy);
  useEffect(() => {
    globalGroupByRef.current = globalGroupBy;
  }, [globalGroupBy]);

  // Keep a ref to entitiesInGroup so event handlers can resolve a
  // representative member UWI for group entities without re-registering
  // listeners on every context update.
  const entitiesInGroupRef = useRef(entitiesInGroup);
  useEffect(() => {
    entitiesInGroupRef.current = entitiesInGroup;
  }, [entitiesInGroup]);

  const txnIdRef = useRef(txnId);
  useEffect(() => {
    txnIdRef.current = txnId;
  }, [txnId]);

  const queryClientRef = useRef(queryClient);
  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  const isAbsentShapefileField = useIsShapefileFieldAbsentFromProject();

  const fieldsRef = useRef({
    facility: defaults?.facilityMapTooltipFields?.fields || [],
    well: defaults?.mapTooltipFields?.fields || []
  });
  useEffect(() => {
    // The persisted list is a global user setting, so it carries shapefile
    // field keys whose shapefile is not in the selected project - deleted, or
    // picked in another project. Those would otherwise be requested and
    // rendered as junk rows (EVA-126).
    fieldsRef.current = {
      facility: withoutAbsentShapefileFields(
        defaults?.facilityMapTooltipFields?.fields,
        isAbsentShapefileField
      ),
      well: withoutAbsentShapefileFields(
        defaults?.mapTooltipFields?.fields,
        isAbsentShapefileField
      )
    };
  }, [defaults, isAbsentShapefileField]);

  // ── Column metadata (for display-label resolution) ───────────────────

  const columns = useColumns();
  const facilityColumns = useFacilityColumns();
  const resolveShapefileColumn = useResolveShapefileColumn();

  // ── Local state ──────────────────────────────────────────────────────

  const popupRef = useRef<HTMLDivElement>(null);
  const [wellDetail, setWellDetail] = useState<WellDetailState>(INITIAL_WELL_DETAIL);

  // Keep a ref to the latest wellDetail so event handlers always see
  // the current value without needing it in the effect dependency array
  // (which would cause listener re-registration on every state change).
  const wellDetailRef = useRef(wellDetail);
  useEffect(() => {
    wellDetailRef.current = wellDetail;
  }, [wellDetail]);

  // The preview popup is the deliberate, pinned reading of one well, and
  // nothing else on the map writes a second one beside it. Only the tooltip
  // stands down, and for every well rather than for the popup's own: the
  // hover itself runs on, so the highlight and the pointer cursor, which is
  // keyed off the hover, are unchanged.
  const previewOpen = useWellCardPanelStore(selectWellCardPreviewOpen);

  // ── Core update function ─────────────────────────────────────────────

  // The newest update, so a fetch that lands after a later hide, or after a
  // move to another well, cannot show a well the cursor has already left.
  const latestUpdateRef = useRef({ id: 0, uwi: "", show: false });

  const updateWellDetailRef = useRef(
    async ({
      position,
      show,
      uwi,
      isFacility,
      groupPrefix,
      isGroupEntity,
      isStick
    }: {
      position: { x: number; y: number };
      show: boolean;
      uwi: string;
      isFacility: boolean;
      /** When present, these key-value pairs are prepended to the API-returned
       *  fields. Used for group entities to show the group name above the
       *  standard well detail fields fetched from the API. */
      groupPrefix?: Record<string, string>;
      /** When true, calls the map-svc group-detail endpoint which resolves
       *  the bin's member UWIs from the cached map layer via lookup IDs. */
      isGroupEntity?: boolean;
      /** When true, the hovered feature is a Pad Generator stick. */
      isStick?: boolean;
    }) => {
      const updateId = latestUpdateRef.current.id + 1;
      latestUpdateRef.current = { id: updateId, uwi, show };
      const newWellDetail: MutableWellDetail = {
        formattedUwi: uwi,
        uwi,
        show,
        x: 0,
        y: 0,
        fields: {},
        isFacility,
        isGroup: Boolean(isGroupEntity)
      };

      if (!show) {
        setWellDetail(newWellDetail);
        return false;
      }

      const fields = isFacility ? fieldsRef.current.facility : fieldsRef.current.well;

      // Group entities: call the map-svc group-detail endpoint which resolves
      // the bin's member UWIs from the cached map layer via lookup IDs,
      // fetches aggregated data from data-svc, and replaces Header.EntityName
      // with the group display name.
      // Single wells/facilities: call the standard well-detail endpoint.
      const currentTxnId = txnIdRef.current?.id;
      const response =
        isGroupEntity && currentTxnId
          ? await fetchGroupDetail({
              txnId: currentTxnId,
              uwi,
              fields,
              defaultSignificantFigures:
                significantFiguresSettingsRef.current.defaultSignificantFigures,
              useStrictRounding: significantFiguresSettingsRef.current.useStrictRounding,
              // mode is the sub-groupBy (e.g. "Operator") when binning is active.
              // When there's no mode, the focus IS the groupBy so fall back to it.
              groupByProperty:
                globalGroupByRef.current?.mode?.property ??
                globalGroupByRef.current?.property,
              groupByTitle:
                globalGroupByRef.current?.mode?.title ?? globalGroupByRef.current?.title
            })
          : await fetchWellDetail({
              uwi,
              isFacility,
              fields,
              defaultSignificantFigures:
                significantFiguresSettingsRef.current.defaultSignificantFigures,
              useStrictRounding: significantFiguresSettingsRef.current.useStrictRounding
            });

      // A newer request for this same well keeps the result: dropping it would
      // only make the tooltip wait for the second fetch.
      const latest = latestUpdateRef.current;
      if (latest.id !== updateId && (!latest.show || latest.uwi !== uwi)) return false;

      if (response.status === 200) {
        let apiFields = response.data.fields || {};
        // A stick's name can come back N/A, blank, or absent (data-svc-rs answers 200
        // with no fields when its lookup fails). The well list is the source of a
        // stick's display name; fall back to the uwi when no cached page has it.
        const entityName = apiFields[ENTITY_NAME_PROPERTY];
        if (
          isStick &&
          (typeof entityName !== "string" ||
            entityName.trim() === "" ||
            entityName.trim().toLowerCase() === NOT_AVAILABLE)
        ) {
          const label =
            findWellListLabel(
              queryClientRef.current.getQueriesData<AxiosResponse<WellListResult>>([
                WELL_LIST_QUERY_KEY
              ]),
              uwi,
              txnIdRef.current?.id
            ) ?? uwi;
          apiFields = { ...apiFields, [ENTITY_NAME_PROPERTY]: label };
        }
        // For group entities, prepend the group context fields (e.g.,
        // "Resource Play: Charlie Lake") before the API-returned fields
        // so the tooltip shows what group the hovered entity belongs to.
        newWellDetail.fields = groupPrefix ? { ...groupPrefix, ...apiFields } : apiFields;
        newWellDetail.formattedUwi =
          newWellDetail.fields.formattedUwi?.length > 0
            ? newWellDetail.fields.formattedUwi
            : uwi;
      }

      newWellDetail.x = position.x + TOOLTIP_OFFSET_X;
      newWellDetail.y = position.y - TOOLTIP_OFFSET_Y;
      setWellDetail(newWellDetail);
      return true;
    }
  );

  // Stable reference — the function captures refs, not state, so it
  // never needs to be recreated.
  const updateWellDetail = updateWellDetailRef.current;

  // ── Debounced updater (for showing tooltip) ──────────────────────────

  const updateWellDetailDebounce = useRef(
    _debounce(
      async (args: {
        position: { x: number; y: number };
        show: boolean;
        uwi: string;
        isFacility: boolean;
        groupPrefix?: Record<string, string>;
        memberUwis?: string[];
        isStick?: boolean;
      }) => {
        return await updateWellDetail(args);
      },
      DEBOUNCE_DELAY_MS
    )
  ).current;

  // ── Pointer cursor over a well ───────────────────────────────────────

  // Two of the tools say so in state this hook can subscribe to: the well
  // card's line tool waiting for its two clicks, and the geom-bin lasso waiting
  // for a shape. The rest are watched on the draw control itself, below - the
  // toolbar's lasso keeps "armed" in its own component state, where nothing
  // else can reach it.
  const isDrawArmed = useWellCardPanelStore(selectWellCardDrawArmed);
  const { isLassoSelected } = useGeomBinContext();
  const toolFlagged = isDrawArmed || isLassoSelected;

  /*
   * Watch the draw control for the tools that have no flag.
   *
   * `draw.modechange` alone is not enough: every tool here arms itself through
   * the control's public `changeMode`, which passes a silent flag and so never
   * fires it. What every mode change does do, silent or not, is mark the draw
   * store dirty and render, and a render fires `draw.render`. So that is the
   * event this listens to, with `draw.modechange` kept for the changes the
   * control makes on its own.
   *
   * Cheap: `draw.render` is tied to animation frames, so it arrives at most
   * once a frame and only while something is drawing, and the handler is a
   * class read that writes state only when the answer actually flips. No
   * polling, no feature query, nothing added to mousemove.
   */
  const [drawToolArmed, setDrawToolArmed] = useState(false);
  const drawToolArmedRef = useRef(drawToolArmed);
  useEffect(() => {
    if (!mapbox) return;
    const sync = () => {
      const armed = isDrawToolArmed(mapbox);
      if (armed === drawToolArmedRef.current) return;
      drawToolArmedRef.current = armed;
      setDrawToolArmed(armed);
    };
    // A tool can already be armed when this mounts - the map outlives the card
    // and its widgets.
    sync();
    mapbox.on(events.RENDER, sync);
    mapbox.on(events.MODE_CHANGE, sync);
    return () => {
      mapbox.off(events.RENDER, sync);
      mapbox.off(events.MODE_CHANGE, sync);
    };
  }, [mapbox]);

  /*
   * A well under the pointer is clickable, and the cursor says so.
   *
   * Keyed strictly off the tooltip that is already up: the tooltip's own
   * mousemove has done the feature query, so this costs nothing and cannot
   * disagree with what is on screen.
   *
   * Cleared to "default" - the app's baseline, the same value `resetCursor`
   * writes on every map move. An empty string would hand the canvas back to
   * mapbox's own grab hand until the next pan. Not put back to a remembered
   * value either: the draw modes write this same canvas cursor, and a value
   * cached here would be theirs.
   *
   * While a tool is armed the canvas is handed back instead, by clearing the
   * inline cursor rather than leaving a value on it. The tool's crosshair
   * comes from a class on the map container, and an inline cursor on the
   * canvas beats a class - so "write nothing" is not enough here: whatever
   * this effect last wrote would sit on the canvas for the rest of the draw.
   */
  // The value this hook last decided on, kept so a mouse move can put it back.
  // `resetCursor` in useMapLifecycle writes the default straight onto this same
  // canvas a moment after the map settles, so a pointer written while the
  // tooltip is up can be replaced by the arrow - and hovering on over the same
  // well changes nothing here to write it again.
  const hoverCursorRef = useRef("");
  useEffect(() => {
    const canvas = mapbox?.getCanvas?.();
    if (!canvas) return;
    // `drawToolArmed` is what re-runs this the moment a tool arms; the class
    // read beside it is what the decision is actually made on, so a mode change
    // that React has not committed yet cannot be painted over.
    if (toolFlagged || drawToolArmed || isDrawToolArmed(mapbox)) {
      hoverCursorRef.current = "";
      canvas.style.cursor = "";
      return;
    }
    hoverCursorRef.current = wellDetail.show && wellDetail.uwi ? "pointer" : "default";
    canvas.style.cursor = hoverCursorRef.current;
    return () => {
      // A tool can arm between this run and its cleanup, and on unmount there
      // is no re-run to correct a "default" written over its crosshair.
      if (isDrawToolArmed(mapbox)) return;
      canvas.style.cursor = "default";
    };
  }, [mapbox, wellDetail.show, wellDetail.uwi, toolFlagged, drawToolArmed]);

  // ── Re-fetch when precision settings change ──────────────────────────

  useEffect(() => {
    const current = wellDetailRef.current;
    if (current.show && current.uwi) {
      const currentPosition = {
        x: current.x - TOOLTIP_OFFSET_X,
        y: current.y + TOOLTIP_OFFSET_Y
      };
      updateWellDetail({
        position: currentPosition,
        show: true,
        uwi: current.uwi,
        isFacility: current.isFacility
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [significantFiguresSettings]);

  // ── Overflow adjustment ──────────────────────────────────────────────

  useEffect(() => {
    if (popupRef.current == null || !wellDetail || !wellDetail.show) {
      return;
    }

    const element = popupRef.current;
    const popupRect = element.getBoundingClientRect();
    const mapElement = document.getElementById("map");
    if (!mapElement) return;
    const mapRect = mapElement.getBoundingClientRect();

    if (popupRect.right <= mapRect.right && popupRect.bottom <= mapRect.bottom) {
      return;
    }

    if (popupRect.right > mapRect.right) {
      element.style.left = `${
        wellDetail.x - (popupRect.right - mapRect.right + MARGIN_FROM_VIEWPORT)
      }px`;
    }

    if (popupRect.bottom > mapRect.bottom) {
      element.style.top = `${
        wellDetail.y - (popupRect.bottom - mapRect.bottom + MARGIN_FROM_VIEWPORT)
      }px`;
    }
  }, [wellDetail]);

  // ── Mapbox event listeners ───────────────────────────────────────────

  useEffect(() => {
    if (!mapbox) return;

    const handleGeneralMouseMove = async (e: mapboxgl.MapMouseEvent) => {
      if (!mapbox) return;
      // Put the hover cursor back if another writer has been over it since the
      // last move - see `hoverCursorRef`.
      const canvas = mapbox.getCanvas?.();
      if (canvas && canvas.style.cursor !== hoverCursorRef.current) {
        canvas.style.cursor = hoverCursorRef.current;
      }
      let props: Maybe<Record<string, unknown>>;
      const buffer = 2;
      const bbox = [
        [e.point.x - buffer, e.point.y - buffer],
        [e.point.x + buffer, e.point.y + buffer]
      ] as [PointLike, PointLike];

      if (!mapbox.getLayer(WELL_LAYER) && !mapbox.getLayer(WELL_LAYER_POINT)) {
        return;
      }

      const layerIds = [
        FACILITY_LAYER,
        SPIDER_FACILITY_LAYER,
        WELL_LAYER,
        WELL_LAYER_POINT,
        STICK_LAYER
      ].filter((id) => mapbox.getLayer(id));

      const features = mapbox.queryRenderedFeatures(bbox, {
        layers: layerIds
      });

      let isFacility = false;
      let isStick = false;
      if (features.length > 0) {
        props = features[0].properties;
        const lid = features[0].layer.id;
        isFacility = lid === FACILITY_LAYER || lid === SPIDER_FACILITY_LAYER;
        isStick = lid === STICK_LAYER;
      }
      const uwi = (props?.Uwi as string) ?? "";
      const groupName = (props?.group as string) ?? "";
      const isGroupEntity =
        groupName.length > 0 && !GROUP_SHAPE_DISCRIMINATORS.has(groupName);

      const current = wellDetailRef.current;

      if (mapbox.getZoom() < MIN_ZOOM_FOR_TOOLTIP || current.uwi === uwi || !uwi) {
        if (!uwi) {
          // Hide tooltip immediately when no feature is found
          await updateWellDetail({
            position: e.point,
            show: false,
            uwi,
            isFacility
          });
          updateWellDetailDebounce.cancel();
        }
        return;
      }

      // Group entity — resolve a representative member UWI from entitiesInGroup
      // so we can call the standard fetchWellDetail API and show real well fields.
      // The group name (focus bin label) is prepended to those API fields.
      if (isGroupEntity) {
        // groupName arrives as "(N) Title" from GroupByResult.group_title.
        // Strip the count prefix to show just the human-readable bin label.
        const countMatch = groupName.match(/^\((\d+)\)\s+(.+)$/);
        const displayName = countMatch ? countMatch[2] : groupName;

        // Determine the focus field title (e.g. "Resource Play", "Operator")
        const focusTitle = globalGroupByRef.current?.title || "Group";

        // Build prefix fields that will be prepended to the API response
        const groupPrefix: Record<string, string> = { [focusTitle]: displayName };

        // The map-svc endpoint resolves the bin's member UWIs from the cached
        // map layer using the group entity's Uwi (numeric group ID) and its
        // lookup IDs. No need to pass member UWIs from the frontend.
        if (uwi) {
          await updateWellDetailDebounce({
            position: e.point,
            show: true,
            uwi,
            isFacility: false,
            groupPrefix,
            isGroupEntity: true
          });
        } else {
          // Fallback: no member UWIs available — show group name only
          updateWellDetailDebounce.cancel();
          await updateWellDetailRef.current({
            position: e.point,
            show: true,
            uwi,
            isFacility: false,
            groupPrefix
          });
        }
        return;
      }

      // Only use debounce for showing the tooltip, not hiding it
      await updateWellDetailDebounce({
        position: e.point,
        show: true,
        uwi,
        isFacility,
        isStick
      });
    };

    // Map movement/change event handlers - hide tooltip immediately
    const handleMapMoveStart = () => {
      updateWellDetail(HIDDEN_DETAIL_ARGS);
      updateWellDetailDebounce.cancel();
    };

    const handleMapMove = () => {
      if (wellDetailRef.current.show) {
        updateWellDetail(HIDDEN_DETAIL_ARGS);
      }
    };

    // Immediate tooltip hiding when mouse leaves feature areas
    const handleMouseLeave = () => {
      if (wellDetailRef.current.show) {
        updateWellDetail(HIDDEN_DETAIL_ARGS);
        updateWellDetailDebounce.cancel();
      }
    };

    // Leaving the canvas straight off a feature fires no further mousemove, and the
    // layer mouseleaves below exist only for layers that were loaded at registration.
    // Unconditional, so a pending show or an in-flight fetch is dropped too.
    const handleMouseOut = () => {
      updateWellDetailDebounce.cancel();
      updateWellDetail(HIDDEN_DETAIL_ARGS);
    };

    // General mousemove event (no layer-specific conflicts)
    mapbox.on(MouseInteraction.MouseMove, handleGeneralMouseMove);
    mapbox.on(MouseInteraction.MouseOut, handleMouseOut);

    // Mouseleave handlers for immediate tooltip hiding
    const layerIds = [
      FACILITY_LAYER,
      SPIDER_FACILITY_LAYER,
      WELL_LAYER,
      WELL_LAYER_POINT
    ].filter((id) => mapbox.getLayer(id));

    layerIds.forEach((layerId) => {
      mapbox.on(MouseInteraction.MouseLeave, layerId, handleMouseLeave);
    });

    mapbox.on(MapInteraction.MoveStart, handleMapMoveStart);
    mapbox.on(MapInteraction.Move, handleMapMove);
    mapbox.on(MapInteraction.ZoomStart, handleMapMoveStart);
    mapbox.on(MapInteraction.RotateStart, handleMapMoveStart);
    mapbox.on(MapInteraction.PitchStart, handleMapMoveStart);

    return () => {
      mapbox.off(MouseInteraction.MouseMove, handleGeneralMouseMove);
      mapbox.off(MouseInteraction.MouseOut, handleMouseOut);

      // No getLayer() guard here: on logout / route-away the map is torn
      // down via .remove(), which deletes its internal style. getLayer()
      // reads that style and throws on a removed map (EVA-WEB-UI-11F).
      // .off() only touches the listener registry, so it is safe and
      // idempotent on a removed map — remove listeners unconditionally,
      // matching the sibling map adapters.
      layerIds.forEach((layerId) => {
        mapbox.off(MouseInteraction.MouseLeave, layerId, handleMouseLeave);
      });

      mapbox.off(MapInteraction.MoveStart, handleMapMoveStart);
      mapbox.off(MapInteraction.Move, handleMapMove);
      mapbox.off(MapInteraction.ZoomStart, handleMapMoveStart);
      mapbox.off(MapInteraction.RotateStart, handleMapMoveStart);
      mapbox.off(MapInteraction.PitchStart, handleMapMoveStart);

      updateWellDetailDebounce.cancel();
    };
    // We use refs for wellDetail access inside handlers, so we only need
    // to re-register when mapbox or significantFiguresSettings change
    // (matching the original component's dependency array).
  }, [mapbox, significantFiguresSettings]);

  // ── Derived view model ─────────────────────────────────────────────

  const isMapReady = mapbox != null;

  const displayFields = useMemo<ReadonlyArray<WellDetailDisplayField>>(() => {
    const seen = new Set<string>();
    return Object.entries(wellDetail.fields)
      .filter(([key]) => key !== "formattedUwi")
      .reduce<WellDetailDisplayField[]>((acc, [key, value]) => {
        // Covers a response already in flight when the shapefile went away or
        // the project switched - its keys are in state from before the request
        // was filtered (EVA-126).
        if (isAbsentShapefileField(key)) return acc;
        // Keys containing a dot (e.g. "Header.Operator_Short") are property
        // codes that must be resolved to human-readable display labels via
        // getColumnName. Shapefile fields are also codes but use underscores
        // (e.g. "Shapefile_<projectId>_<shapefileId>_<base64>"), so route those
        // through the resolver too. Keys without a dot (e.g. "Resource Play",
        // "Operator") are group-prefix labels already in display form — pass through.
        const isPropertyCode = key.includes(".") || key.startsWith("Shapefile_");
        // Shapefile fields aren't in the live column list unless the user has
        // picked the shapefile in group-by, so resolve the name from project
        // shapefile data. Failing that - no tree loaded yet, or a titleless
        // node - name the field from the key itself, so the raw
        // "Shapefile_..." string is never the label.
        const resolvedShapefileColumn = key.startsWith("Shapefile_")
          ? resolveShapefileColumn(key)
          : null;
        const shapefileColumn = key.startsWith("Shapefile_")
          ? resolvedShapefileColumn ?? shapefileColumnFromKey(key)
          : null;
        // Prepended to the list getColumnName reads, which it picks by
        // isFacility - prepending to only the well list left facility hovers
        // falling through to the raw key.
        const withShapefileColumn = (list: typeof columns) =>
          shapefileColumn ? [shapefileColumn, ...list] : list;
        const label = isPropertyCode
          ? getColumnName({
              property: key,
              isFacility: wellDetail.isFacility,
              facilityColumns: wellDetail.isFacility
                ? withShapefileColumn(facilityColumns)
                : facilityColumns,
              columns: wellDetail.isFacility ? columns : withShapefileColumn(columns)
            })
          : key;
        // In group mode the backend can return multiple keys that resolve to
        // the same display label (e.g. a dot-notation property key and a
        // group-prefix key both becoming "Operator"). Keep only the first -
        // except when the label is the bare field name from the last-resort
        // key parse: two unresolvable shapefiles can both carry a "County",
        // and deduping on that shared name would silently drop the second
        // value, so those dedupe on the full key instead.
        const dedupeKey = shapefileColumn && !resolvedShapefileColumn ? key : label;
        if (seen.has(dedupeKey)) return acc;
        seen.add(dedupeKey);
        acc.push({ label, value });
        return acc;
      }, []);
  }, [
    wellDetail.fields,
    wellDetail.isFacility,
    wellDetail.isGroup,
    columns,
    facilityColumns,
    resolveShapefileColumn,
    isAbsentShapefileField
  ]);

  const viewModel = useMemo<WellDetailViewModel>(
    () => ({
      show: isMapReady && wellDetail.show && !previewOpen,
      x: wellDetail.x,
      y: wellDetail.y,
      isFacility: wellDetail.isFacility,
      isGroup: wellDetail.isGroup,
      displayFields
    }),
    [
      isMapReady,
      previewOpen,
      wellDetail.show,
      wellDetail.x,
      wellDetail.y,
      wellDetail.isFacility,
      wellDetail.isGroup,
      displayFields
    ]
  );

  return { wellDetail: viewModel, popupRef };
}
